import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatMYR, formatDate } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Trash2, Pencil, Phone, History, UserRound } from "lucide-react";

const emptyForm = { name: "", phone: "", email: "", address: "", note: "", birthday: "", anniversary: "" };

const TIER_META = {
    silver: { label: "银卡", cls: "bg-slate-200 text-slate-700" },
    gold: { label: "金卡", cls: "bg-gold-200 text-gold-800" },
    diamond: { label: "钻卡", cls: "bg-purple-200 text-purple-800" },
};

export default function CustomersPage() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [q, setQ] = useState(searchParams.get("q") || "");

    // Sync q ↔ URL so the command palette can deep-link
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        if (urlQ !== q) setQ(urlQ);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        if (q && q !== urlQ) setSearchParams({ q }, { replace: true });
        else if (!q && urlQ) setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create, obj = edit
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const [historyOf, setHistoryOf] = useState(null);
    const [historySales, setHistorySales] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/customers");
            setCustomers(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "加载失败");
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (c) => {
        setEditing(c);
        setForm({
            name: c.name || "",
            phone: c.phone || "",
            email: c.email || "",
            address: c.address || "",
            note: c.note || "",
            birthday: c.birthday || "",
            anniversary: c.anniversary || "",
        });
        setDialogOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("请填写客户姓名");
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/customers/${editing.id}`, form);
                toast.success("客户已更新");
            } else {
                await api.post("/customers", form);
                toast.success("客户已创建");
            }
            setDialogOpen(false);
            setForm(emptyForm);
            setEditing(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    const del = async (c) => {
        if (!window.confirm(`确定删除客户 ${c.name}？（历史销售记录会保留，仅解除关联）`)) return;
        try {
            await api.delete(`/customers/${c.id}`);
            toast.success("客户已删除");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "删除失败");
        }
    };

    const openHistory = async (c) => {
        setHistoryOf(c);
        setHistoryLoading(true);
        try {
            const { data } = await api.get(`/customers/${c.id}/sales`);
            setHistorySales(data);
        } catch (err) {
            toast.error("加载历史失败");
            setHistorySales([]);
        }
        setHistoryLoading(false);
    };

    const filtered = useMemo(() => {
        if (!q) return customers;
        const t = q.toLowerCase();
        return customers.filter((c) =>
            (c.name || "").toLowerCase().includes(t) ||
            (c.phone || "").toLowerCase().includes(t) ||
            (c.email || "").toLowerCase().includes(t)
        );
    }, [customers, q]);

    const totalCustomers = customers.length;
    const totalSpent = customers.reduce((s, c) => s + Number(c.total_spent || 0), 0);
    const activeCount = customers.filter((c) => (c.order_count || 0) > 0).length;

    return (
        <div className="space-y-6" data-testid="customers-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">CRM</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">客户管理</h1>
                    <p className="text-sm text-gold-500 mt-1">管理客户信息、查看消费记录与购买历史</p>
                </div>
                <Button data-testid="add-customer-btn" onClick={openCreate} className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                    <Plus className="h-4 w-4" /> 添加客户
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">客户总数</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{totalCustomers}</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">有消费客户</div>
                    <div className="text-2xl font-bold font-mono text-slate-900 mt-2">{activeCount}</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">累计消费</div>
                    <div className="text-2xl font-bold font-mono text-gold-600 mt-2">{formatMYR(totalSpent)}</div>
                </Card>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="customers-search-input"
                            placeholder="搜索姓名、电话、邮箱..."
                            className="pl-9"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-gold-500">加载中...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <UserRound className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-sm text-gold-500">{customers.length === 0 ? "还没有客户记录" : "没有匹配的客户"}</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-gold-50/60">
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">姓名</th>
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">电话</th>
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">地址</th>
                                    <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">订单数</th>
                                    <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">累计消费</th>
                                    <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">最后购买</th>
                                    <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-slate-100 hover:bg-gold-50 transition-colors duration-150">
                                        <td className="py-3 px-6">
                                            <div className="flex items-center gap-3">
                                                <div className="h-8 w-8 rounded-full bg-gold-100 text-gold-700 flex items-center justify-center text-xs font-bold">
                                                    {(c.name || "?").slice(0, 1).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-slate-900 inline-flex items-center gap-2">
                                                        {c.name}
                                                        {c.tier && TIER_META[c.tier] && (
                                                            <Badge className={`${TIER_META[c.tier].cls} border-0 text-[10px] font-bold`}>{TIER_META[c.tier].label}</Badge>
                                                        )}
                                                    </div>
                                                    {c.birthday && <div className="text-[10px] text-gold-600 mt-0.5 font-mono">🎂 {c.birthday}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-6 text-sm text-slate-700 font-mono">
                                            {c.phone ? (
                                                <span className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3 text-slate-400" /> {c.phone}</span>
                                            ) : <span className="text-slate-400">-</span>}
                                        </td>
                                        <td className="py-3 px-6 text-sm text-slate-600 max-w-[240px] truncate">{c.address || <span className="text-slate-400">-</span>}</td>
                                        <td className="py-3 px-6 text-sm text-right font-mono text-slate-700">
                                            {c.order_count > 0 ? (
                                                <Badge className="bg-gold-100 text-gold-800 hover:bg-gold-100 border-0 font-mono">{c.order_count}</Badge>
                                            ) : <span className="text-slate-400">0</span>}
                                        </td>
                                        <td className="py-3 px-6 text-sm text-right font-mono font-semibold text-gold-700">{formatMYR(c.total_spent || 0)}</td>
                                        <td className="py-3 px-6 text-sm text-gold-500 font-mono">{c.last_purchase_at ? formatDate(c.last_purchase_at) : <span className="text-slate-400">-</span>}</td>
                                        <td className="py-3 px-6 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    data-testid={`customer-history-${c.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={!c.order_count}
                                                    onClick={() => openHistory(c)}
                                                    className="text-slate-600 hover:text-gold-700 hover:bg-gold-50 gap-1.5 disabled:opacity-30"
                                                    title="购买历史"
                                                >
                                                    <History className="h-3.5 w-3.5" />
                                                    <span className="hidden lg:inline text-xs">历史</span>
                                                </Button>
                                                <Button
                                                    data-testid={`customer-edit-${c.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEdit(c)}
                                                    className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    data-testid={`customer-delete-${c.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => del(c)}
                                                    className="text-red-600 hover:bg-red-50"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Create / Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md" data-testid="customer-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "编辑客户" : "添加客户"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4 pt-2">
                        <div className="space-y-1.5">
                            <Label>姓名 *</Label>
                            <Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label>电话</Label>
                            <Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+60 12-345 6789" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>邮箱</Label>
                            <Input data-testid="customer-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>生日 🎂</Label>
                                <Input
                                    data-testid="customer-birthday-input"
                                    type="date"
                                    value={form.birthday}
                                    onChange={(e) => setForm({ ...form, birthday: e.target.value })}
                                    className="font-mono"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>纪念日 💍</Label>
                                <Input
                                    type="date"
                                    value={form.anniversary}
                                    onChange={(e) => setForm({ ...form, anniversary: e.target.value })}
                                    className="font-mono"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>地址</Label>
                            <Textarea rows={2} data-testid="customer-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                            <Label>备注</Label>
                            <Textarea rows={2} data-testid="customer-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                        </div>
                        <DialogFooter className="pt-2">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
                            <Button type="submit" data-testid="save-customer-btn" disabled={saving} className="bg-gold-600 hover:bg-gold-700 text-white">
                                {saving ? "保存中..." : editing ? "保存修改" : "创建客户"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* History dialog */}
            <Dialog open={!!historyOf} onOpenChange={(o) => !o && setHistoryOf(null)}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="customer-history-dialog">
                    <DialogHeader>
                        <DialogTitle>{historyOf?.name} · 购买历史</DialogTitle>
                    </DialogHeader>
                    {historyOf && (
                        <div className="pt-2 space-y-3">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gold-500 border-b border-slate-200 pb-3">
                                <span>电话：<span className="text-slate-800 font-medium font-mono">{historyOf.phone || "-"}</span></span>
                                <span>订单数：<span className="text-slate-800 font-medium font-mono">{historyOf.order_count || 0}</span></span>
                                <span>累计消费：<span className="text-gold-700 font-bold font-mono">{formatMYR(historyOf.total_spent || 0)}</span></span>
                            </div>
                            {historyLoading ? (
                                <div className="py-10 text-center text-gold-500">加载中...</div>
                            ) : historySales.length === 0 ? (
                                <div className="py-10 text-center text-gold-500 text-sm">暂无购买记录</div>
                            ) : (
                                <div className="border border-slate-200 rounded-md overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gold-50 border-b border-slate-200">
                                                <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gold-500">发票号</th>
                                                <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gold-500">日期</th>
                                                <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gold-500">商品</th>
                                                <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gold-500">总额</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historySales.map((s) => (
                                                <tr key={s.id} className="border-b border-slate-100 last:border-0">
                                                    <td className="py-2 px-3 font-mono font-semibold text-slate-900">{s.invoice_no}</td>
                                                    <td className="py-2 px-3 text-slate-600 font-mono">{formatDate(s.created_at)}</td>
                                                    <td className="py-2 px-3 text-slate-700">{s.items.map((it) => `${it.product_name} × ${it.quantity}`).join("、")}</td>
                                                    <td className="py-2 px-3 text-right font-mono font-bold text-gold-600">{formatMYR(s.total)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
